import { UpdateFrequency } from '@kanbu/schema/enums';
import {
  getCommonWebsitePathScope,
  getNormalizedWebsiteHost,
  normalizeWebsitePathScope,
  urlMatchesPathScope,
} from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Checkbox,
  Dialog,
  Input,
  Label,
  Select,
  SelectItem,
  toast,
} from '@utima/ui';
import { useEffect, useMemo, useRef, useState } from 'react';

import { LockedFeatureIcon } from '@/components';
import { Alert } from '@/components/Alert';
import { OverlappingRootsAlert } from '@/features/documents/components/OverlappingRootsAlert';
import { UPDATE_FREQUENCY_OPTIONS } from '@/features/documents/lib/updateFrequencyOptions';
import { useEnumTranslations } from '@/hooks';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { trpc } from '@/services/trpc';

import type { KBDocumentData } from './kbTypes';
import type { useKBMutations } from '../hooks/useKBMutations';
import { useKBWebsiteHierarchyLabels } from '../hooks/useKBWebsiteHierarchyLabels';

type PromoteToTrackedWebsiteModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: KBDocumentData[];
  mutations: ReturnType<typeof useKBMutations>;
  onFinished?: () => void;
};

function resolveDocumentUrl(document: KBDocumentData): string | null {
  return document.uri ?? document.publicUrl ?? null;
}

function resolveDocumentHost(document: KBDocumentData): string | null {
  const rawUrl = resolveDocumentUrl(document);
  if (!rawUrl) {
    return null;
  }

  try {
    return getNormalizedWebsiteHost(rawUrl);
  } catch {
    return null;
  }
}

function suggestPathPrefix(docs: KBDocumentData[]): string {
  const urls = docs
    .map(resolveDocumentUrl)
    .filter((url): url is string => !!url);

  return getCommonWebsitePathScope(urls);
}

export function PromoteToTrackedWebsiteModal({
  open,
  onOpenChange,
  documents,
  mutations,
  onFinished,
}: PromoteToTrackedWebsiteModalProps) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const { isKbAutoUpdatesLocked } = useSubscriptionFeatures();
  const websiteHierarchyLabels = useKBWebsiteHierarchyLabels();

  const groups = useMemo(() => {
    const grouped = new Map<string, KBDocumentData[]>();

    for (const document of documents) {
      const host = resolveDocumentHost(document);
      if (!host) {
        continue;
      }

      const current = grouped.get(host) ?? [];
      current.push(document);
      grouped.set(host, current);
    }

    return [...grouped.entries()].map(([host, docs]) => ({
      host,
      docs,
    }));
  }, [documents]);

  const [selectedHost, setSelectedHost] = useState<string>('');
  const [pathPrefix, setPathPrefix] = useState('');
  const [moveAllHostDocuments, setMoveAllHostDocuments] = useState(false);

  const [discoveryFrequency, setDiscoveryFrequency] = useState(
    UpdateFrequency.Never,
  );

  const initializedForOpen = useRef(false);

  useEffect(() => {
    if (!open) {
      initializedForOpen.current = false;

      return;
    }

    if (initializedForOpen.current || groups.length === 0) {
      return;
    }

    initializedForOpen.current = true;
    setSelectedHost(groups[0]?.host ?? '');
    setPathPrefix(groups[0] ? suggestPathPrefix(groups[0].docs) : '');
    setMoveAllHostDocuments(false);
    setDiscoveryFrequency(UpdateFrequency.Never);
  }, [open, groups]);

  useEffect(() => {
    if (isKbAutoUpdatesLocked) {
      setDiscoveryFrequency(UpdateFrequency.Never);
    }
  }, [isKbAutoUpdatesLocked]);

  const selectedGroup =
    groups.find(group => group.host === selectedHost) ?? null;

  const normalizedPathPrefix = normalizeWebsitePathScope(pathPrefix);
  const documentsInScope = useMemo(() => {
    if (!selectedGroup) {
      return [];
    }

    return selectedGroup.docs.filter(doc => {
      const url = resolveDocumentUrl(doc);

      return !!url && urlMatchesPathScope(url, normalizedPathPrefix);
    });
  }, [selectedGroup, normalizedPathPrefix]);

  const trackedRootPreview = selectedGroup
    ? `https://${selectedGroup.host}${normalizedPathPrefix}`
    : '';

  const { data: overlapCheck } =
    trpc.documentFolders.checkWebsiteRootOverlap.useQuery(
      { url: trackedRootPreview },
      { enabled: open && !!trackedRootPreview },
    );

  const handleHostChange = (host: string) => {
    setSelectedHost(host);
    const group = groups.find(item => item.host === host);
    setPathPrefix(group ? suggestPathPrefix(group.docs) : '');
  };

  const handleSubmit = async () => {
    if (!selectedGroup || documentsInScope.length === 0) {
      return;
    }

    try {
      const result = await mutations.promoteToTrackedWebsite.mutateAsync({
        documentIds: documentsInScope.map(doc => doc.id),
        targetHost: selectedGroup.host,
        pathPrefix: normalizedPathPrefix,
        moveAllHostDocuments,
        ...(!overlapCheck?.exactMatch && {
          discoveryFrequency: isKbAutoUpdatesLocked
            ? UpdateFrequency.Never
            : discoveryFrequency,
        }),
      });

      toast.success(
        t`${result.moved} documents moved under tracked website hierarchy`,
      );
      onOpenChange(false);
      onFinished?.();
    } catch {
      toast.error(t`Failed to add documents to tracked website`);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='w-[560px]'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Promote to website hierarchy</Trans>
          </Dialog.Title>
          <Dialog.Description>
            {websiteHierarchyLabels.promoteToWebsiteHierarchyHint}
          </Dialog.Description>
        </Dialog.Header>

        <div className='space-y-4 py-4'>
          <div className='space-y-1.5'>
            <p className='text-sm font-medium'>
              <Trans>Host</Trans>
            </p>
            <Select value={selectedHost} onValueChange={handleHostChange}>
              {groups.map(group => (
                <SelectItem key={group.host} value={group.host}>
                  {group.host} ({group.docs.length})
                </SelectItem>
              ))}
            </Select>
          </div>

          <div className='space-y-1.5'>
            <p className='text-sm font-medium'>
              <Trans>Path prefix</Trans>
            </p>
            <Input
              value={pathPrefix}
              onChange={event => setPathPrefix(event.target.value)}
              placeholder={t`/blog (optional — empty = whole site)`}
            />
            {trackedRootPreview ? (
              <p className='text-muted-foreground text-xs'>
                <Trans>Tracked root:</Trans> {trackedRootPreview}
                {selectedGroup &&
                documentsInScope.length !== selectedGroup.docs.length ? (
                  <>
                    {' · '}
                    <Trans>
                      {documentsInScope.length} of {selectedGroup.docs.length}{' '}
                      selected pages match this path
                    </Trans>
                  </>
                ) : null}
              </p>
            ) : null}
          </div>

          {overlapCheck?.exactMatch ? (
            <Alert
              variant='info'
              title={t`Existing tracked root`}
              description={t`Documents will be added under the existing root ${overlapCheck.exactMatch.websiteUrl}. Its existing settings will be preserved.`}
            />
          ) : (
            <div className='space-y-1.5'>
              <div className='flex items-center gap-2'>
                <Label htmlFor='promoteDiscoveryFrequency'>
                  <Trans>Look for new pages</Trans>
                </Label>
                <LockedFeatureIcon locked={isKbAutoUpdatesLocked} />
              </div>
              <Select
                id='promoteDiscoveryFrequency'
                value={discoveryFrequency}
                disabled={isKbAutoUpdatesLocked}
                onValueChange={value =>
                  setDiscoveryFrequency(value as UpdateFrequency)
                }
              >
                <SelectItem value={UpdateFrequency.Never}>
                  <Trans>Off</Trans>
                </SelectItem>
                {UPDATE_FREQUENCY_OPTIONS.filter(
                  value => value !== UpdateFrequency.Never,
                ).map(frequency => (
                  <SelectItem key={frequency} value={frequency}>
                    {enumTranslations.updateFrequency[frequency]}
                  </SelectItem>
                ))}
              </Select>
            </div>
          )}
          {overlapCheck ? (
            <OverlappingRootsAlert
              overlappingRoots={overlapCheck.overlappingRoots}
            />
          ) : null}

          <label className='flex items-start gap-2 text-sm'>
            <Checkbox
              checked={moveAllHostDocuments}
              onCheckedChange={checked =>
                setMoveAllHostDocuments(Boolean(checked))
              }
              className='mt-0.5'
            />
            <span>
              {normalizedPathPrefix ? (
                <Trans>
                  Also move all existing website documents under this path to
                  build a clean hierarchy.
                </Trans>
              ) : (
                <Trans>
                  Also move all existing website documents from this host to
                  build a clean hierarchy.
                </Trans>
              )}
            </span>
          </label>
        </div>

        <Dialog.Footer>
          <Button variant='ghost' outline onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedGroup || documentsInScope.length === 0}
            loading={mutations.promoteToTrackedWebsite.isPending}
          >
            <Trans>Promote</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
